// <collection-view>: view switcher, Load More and sub-collection swap; first-paint columns live in collection-view-init.

const COLLECTION_MOBILE_QUERY = '(max-width: 991px)';

const COLLECTION_REFLOW_DURATION = 420;
const COLLECTION_REFLOW_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

// Cards off screen in both layouts just jump; Load More can push the grid past 100.
const COLLECTION_REFLOW_VIEWPORT_MARGIN = 600;

// Below this a position change is layout noise, not a move.
const COLLECTION_MOVE_THRESHOLD = 4;

const COLLECTION_PREFETCH_CARD_COUNT = 4;
const COLLECTION_PREFETCH_ROOT_MARGIN = '600px';

// A floor on swap time, not a gap: the outgoing grid holds its place at opacity 0.
const COLLECTION_SWAP_FADE = 240;

// Intent delay: a pointer crossing the row shouldn't fetch every link.
const COLLECTION_SUBNAV_HOVER_DELAY = 120;

// Section HTML is no-store and goes stale, so held responses need their own expiry.
const COLLECTION_SUBNAV_CACHE_TTL = 60000;

// Rows of the current view, not a fixed card count (see decisions.md: warmed image count).
const COLLECTION_SWAP_PREFETCH_ROWS = 2;

// Below this the fade reads as the transition; above it the loader shows.
const COLLECTION_SWAP_LOADER_DELAY = 300;

function collectionWait(duration) {
  return new Promise((resolve) => window.setTimeout(resolve, duration));
}

// Metered or slow connection: gates speculative fetches only.
function collectionPrefetchUnwelcome() {
  const connection = navigator.connection;

  if (!connection) return false;

  return connection.saveData === true || /(^|-)[23]g$/.test(connection.effectiveType || '');
}

// Read from <product-card>, so the promo banner (no card) is skipped.
function cardKey(item) {
  return item.querySelector('[data-product-id]')?.dataset.productId ?? null;
}

// The height test excludes a card hidden by the current view (the promo banner).
// see known-issues.md: FLIP layout offsets
function gridOffset(item, grid) {
  return { x: item.offsetLeft - grid.offsetLeft, y: item.offsetTop - grid.offsetTop };
}

// Transform-free page offset, so a card mid-reveal or mid-FLIP measures where it will sit.
function collectionLayoutTop(element) {
  let top = 0;

  for (let node = element; node; node = node.offsetParent) top += node.offsetTop;

  return top;
}

function onScreen(rect) {
  return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

class CollectionView extends HTMLElement {
  connectedCallback() {
    this.mobileQuery = window.matchMedia(COLLECTION_MOBILE_QUERY);
    this.prefetchedPages = new Set();
    this.cancelReflow = null;
    this.observedButton = null;

    // Lets popstate tell a sub-collection change from a filter change.
    this.collectionUrl = window.location.pathname;
    this.swapToken = 0;

    // Holds the in-flight promise so a click during a prefetch awaits it.
    this.subcollectionCache = new Map();
    this.subcollectionHoverTimer = null;

    // Delegated: filtering and Load More replace the buttons.
    this.addEventListener('click', this.handleClick);

    this.handleBreakpoint = () => this.loadView();
    this.mobileQuery.addEventListener('change', this.handleBreakpoint);

    this.handlePopState = () => this.onPopState();
    window.addEventListener('popstate', this.handlePopState);

    // pointerover/focusin bubble, so replaced links stay covered.
    this.handleSubnavIntent = (event) => this.queueSubcollectionPrefetch(event.target);
    this.addEventListener('pointerover', this.handleSubnavIntent);
    this.addEventListener('focusin', this.handleSubnavIntent);

    // Covers touch: pointerdown starts the request while the finger is still down.
    this.handleSubnavCommit = (event) => this.commitSubcollectionPrefetch(event.target);
    this.addEventListener('pointerdown', this.handleSubnavCommit);

    // A cancelled pointer (swipe or scroll) aborts the transfer.
    this.handleSubnavAbort = (event) => this.abortSubcollectionPrefetch(event.target);
    this.addEventListener('pointercancel', this.handleSubnavAbort);

    this.prefetchObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) this.prefetchNextPage(entry.target);
      }),
      { rootMargin: COLLECTION_PREFETCH_ROOT_MARGIN }
    );

    // Filtering replaces the grid and Load More button wholesale.
    this.gridContainer = this.querySelector('#ProductGridContainer');

    if (this.gridContainer) {
      this.renderObserver = new MutationObserver(() => this.handleRender());
      this.renderObserver.observe(this.gridContainer, { childList: true });
    }

    // Image loads, the view switcher and resizes all change the grid's size.
    this.bannerObserver = new ResizeObserver(() => this.syncBannerHeights());

    this.loadView();
    this.syncPrefetchObserver();
    this.observeBannerGrid();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.mobileQuery?.removeEventListener('change', this.handleBreakpoint);
    window.removeEventListener('popstate', this.handlePopState);
    this.removeEventListener('pointerover', this.handleSubnavIntent);
    this.removeEventListener('focusin', this.handleSubnavIntent);
    this.removeEventListener('pointerdown', this.handleSubnavCommit);
    this.removeEventListener('pointercancel', this.handleSubnavAbort);
    window.clearTimeout(this.subcollectionHoverTimer);
    this.renderObserver?.disconnect();
    this.prefetchObserver?.disconnect();
    this.bannerObserver?.disconnect();
    this.cancelReflow?.();
  }

  // Re-queried every time: filtering replaces these elements.
  get grid() {
    return this.querySelector('.collection-product-grid');
  }

  get viewButtons() {
    return this.querySelectorAll('[data-collection-columns]');
  }

  handleClick = (event) => {
    const viewButton = event.target.closest('[data-collection-columns]');

    if (viewButton) {
    event.preventDefault();
      this.selectView(viewButton);
      return;
    }

    const loadMoreButton = event.target.closest('[data-collection-load-more]');

    if (loadMoreButton) {
      event.preventDefault();
      // detail is the click count, and 0 for a click synthesised by Enter/Space.
      this.loadMore(loadMoreButton, { fromKeyboard: event.detail === 0 });
      return;
    }

    const subcollectionLink = event.target.closest('[data-subcollection-link]');

    if (subcollectionLink) {
      // Modified click: leave the href to open a new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      this.selectSubcollection(subcollectionLink);
    }
  };

  // Re-apply the view to the fresh grid before applyRenderReveal measures it.
  handleRender() {
    this.loadView();
    this.applyRenderReveal();
    this.syncPrefetchObserver();
    this.observeBannerGrid();
  }

  /* ------------------------------------------------------------------
   * Promo banner height
   * ------------------------------------------------------------------ */

  observeBannerGrid() {
    const grid = this.grid;

    this.bannerObserver.disconnect();

    if (grid?.querySelector('.grid__item--banner')) this.bannerObserver.observe(grid);
  }

  // Card photos have no fixed ratio, and the info row grows with swatches, so the
  // banner is sized to where the photos in its last row end (see decisions.md).
  syncBannerHeights() {
    const grid = this.grid;

    if (!grid) return;

    const items = Array.from(grid.children);

    items
      .filter((item) => item.classList.contains('grid__item--banner'))
      .forEach((banner) => {
        if (!banner.offsetParent) return;

        const top = collectionLayoutTop(banner);
        const bottom = top + banner.offsetHeight;

        const rowCards = items.filter((item) => {
          if (item === banner || !item.offsetParent || !item.querySelector('.product_card_media_wrapper')) return false;

          const itemTop = collectionLayoutTop(item);

          return itemTop >= top && itemTop < bottom;
        });

        const lastRowTop = Math.max(...rowCards.map(collectionLayoutTop));
        const mediaBottoms = rowCards
          .filter((card) => collectionLayoutTop(card) === lastRowTop)
          .map((card) => {
            const media = card.querySelector('.product_card_media_wrapper');

            return collectionLayoutTop(media) + media.offsetHeight;
          })
          .filter((mediaBottom) => mediaBottom > top);

        if (!mediaBottoms.length) {
          banner.style.removeProperty('--promo-banner-media-height');
          return;
        }

        banner.style.setProperty('--promo-banner-media-height', `${Math.max(...mediaBottoms) - top}px`);
      });
  }

  /* ------------------------------------------------------------------
   * View switcher
   * ------------------------------------------------------------------ */

  resolveView() {
    return window.collectionGridView?.resolve() ?? null;
  }

  selectView(button) {
    const view = this.resolveView();

    if (!view) return;

    const columns = button.dataset.collectionColumns;

    try {
      localStorage.setItem(view.storageKey, columns);
    } catch (error) {
      // Private mode - the choice just won't persist.
    }

    this.setColumns(columns, true);

  requestAnimationFrame(() => {
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.syncFacetDetailsState();
    }
  });
  }

  loadView() {
    const view = this.resolveView();

    // Search results: no switcher, so the stylesheet default stands.
    if (!view || !this.viewButtons.length) return;

    this.setColumns(view.columns);
  }

  // Only a real button press animates; nothing else has a previous layout.
  setColumns(columns, animate) {
    const grid = this.grid;

    if (!grid) return;

    columns = String(columns);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (animate && !reduceMotion) {
      this.reflowColumns(grid, columns);
    } else {
      this.applyColumns(grid, columns);
    }

    this.syncButtons(columns);
  }

  syncButtons(columns) {
    const mobile = this.mobileQuery.matches;

    const active = this.querySelectorAll(
      mobile
        ? '.collection-toolbar__view-mobile [data-collection-columns]'
        : '.collection-toolbar__view-desktop [data-collection-columns]'
    );

    this.viewButtons.forEach((button) => {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
    });

    active.forEach((button) => {
      const isActive = button.dataset.collectionColumns === columns;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // Cancel the reveal on cards this reflow animates, or the two animations clash.
  cancelScrollReveal(item) {
    item.classList.remove('scroll-trigger--offscreen');
    item.classList.add('scroll-trigger--cancel');
  }

  applyColumns(grid, columns) {
    grid.style.setProperty('--collection-grid-columns', columns);

    // On this element because filtering never replaces it; the grid property beats the grid's own default.
    this.setAttribute('data-collection-grid-columns', columns);

    grid.querySelectorAll('product-card-slider').forEach((slider) => {
      // 8-up drops the slider; runs before the height fix-up because reflowColumns measures Last next.
      slider.syncToColumns?.(columns);

      // Simple mode has no Swiper and sizes itself from CSS.
      if (!slider.swiper) return;

      // Clear Swiper's stale autoHeight before measuring, or the FLIP targets the wrong positions.
      slider.swiper.wrapperEl.style.height = '';
      slider.swiper.updateAutoHeight();
    });
  }

  // FLIP via element.animate(): Dawn's forwards-filled reveal outranks an inline transform.
  reflowColumns(grid, columns) {
    // A fast double-click starts clean, not from mid-transform.
    this.cancelReflow?.();

    const items = Array.from(grid.children);
    const top = -COLLECTION_REFLOW_VIEWPORT_MARGIN;
    const bottom = window.innerHeight + COLLECTION_REFLOW_VIEWPORT_MARGIN;
    const offscreen = (rect) => rect.bottom < top || rect.top > bottom;

    // see known-issues.md: FLIP, never scale a whole card
    const media = items.map((item) => item.querySelector('.product_card_media_wrapper'));
    const info = items.map((item) => item.querySelector('.product_card_info'));

    const before = items.map((item) => item.getBoundingClientRect());

    this.applyColumns(grid, columns);

    const after = items.map((item) => item.getBoundingClientRect());

    // Read in the same batch as `after`: it depends on the scaled photo height.
    const mediaHeight = media.map((element) => (element ? element.getBoundingClientRect().height : 0));

    const running = [];

    const play = (element, keyframes) => {
      running.push(
        element.animate(keyframes, {
          duration: COLLECTION_REFLOW_DURATION,
          easing: COLLECTION_REFLOW_EASING,
        })
      );
    };

    items.forEach((item, index) => {
      const a = before[index];
      const b = after[index];

      // Hidden in the view being entered (the promo banner): nothing to animate.
      if (!b.width) return;

      // see known-issues.md: FLIP of a display: none element
      if (!a.width) {
        if (offscreen(b)) return;

        this.cancelScrollReveal(item);

        play(item, [{ transform: 'translateY(100%)' }, { transform: 'none' }]);

        return;
      }

      if (offscreen(a) && offscreen(b)) return;

      const dx = a.left - b.left;
      const dy = a.top - b.top;
      const scale = a.width / b.width;

      if (!dx && !dy && Math.abs(scale - 1) < 0.001) return;

      // This card is on screen for the move, so its reveal would clash.
      this.cancelScrollReveal(item);

      // Non-card items (the promo banner) keep a uniform scale.
      if (!media[index]) {
        play(item, [
          {
            transformOrigin: 'top left',
            transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
          },
          { transformOrigin: 'top left', transform: 'none' },
        ]);
        return;
      }

      play(item, [
        { transform: `translate(${dx}px, ${dy}px)` },
        { transform: 'none' },
      ]);

      play(media[index], [
        { transformOrigin: 'top left', transform: `scale(${scale})` },
        { transformOrigin: 'top left', transform: 'none' },
      ]);

      // Shift the info row with the scaled photo so it does not detach.
      const shift = mediaHeight[index] * (scale - 1);

      if (info[index] && Math.abs(shift) >= 0.5) {
        play(info[index], [
          { transform: `translateY(${shift}px)` },
          { transform: 'none' },
        ]);
      }
    });

    if (!running.length) return;

    this.cancelReflow = () => {
      running.forEach((animation) => animation.cancel());
      this.cancelReflow = null;
    };
  }

  /* ------------------------------------------------------------------
   * Filter / sort re-render
   * ------------------------------------------------------------------ */

  // see decisions.md: filter/sort re-render reveals per card
  captureCardPositions() {
    const grid = this.grid;

    this.previousCardPositions = new Map();

    if (!grid) return;

    Array.from(grid.children).forEach((item) => {
      const key = cardKey(item);

      if (!key) return;

      // Only on-screen cards need a before position.
      if (!onScreen(item.getBoundingClientRect())) return;

      this.previousCardPositions.set(key, gridOffset(item, grid));
    });
  }

  applyRenderReveal() {
    // Consumed once so a later render can't reuse stale positions.
    const previous = this.previousCardPositions;

    this.previousCardPositions = null;

    const grid = this.grid;

    if (!grid || !previous) return;

    // A filter applied while the last move is still running starts clean.
    this.cancelReflow?.();

    // All reads before writes; runs after loadView so the grid has its real column count.
    const items = Array.from(grid.children).filter((item) => item.classList.contains('scroll-trigger'));
    const rects = items.map((item) => item.getBoundingClientRect());
    const offsets = items.map((item) => gridOffset(item, grid));

    const moved = [];
    let order = 0;

    items.forEach((item, index) => {
      const rect = rects[index];

      if (!onScreen(rect)) {
        // --offscreen is the hidden pre-animation state; the observer reveals it on scroll.
        item.classList.add('scroll-trigger--offscreen');
        return;
      }

      const before = previous.get(cardKey(item));

      if (!before) {
        // Re-stamped from 0: the revealed cards are scattered, so Liquid's orders have gaps.
        item.style.setProperty('--animation-order', order);
        order += 1;
        return;
      }

      // Not new, so no reveal - whether or not it also moved.
      this.cancelScrollReveal(item);

      // Grid-relative offsets, so only a real reorder has a delta.
      const dx = before.x - offsets[index].x;
      const dy = before.y - offsets[index].y;

      if (Math.abs(dx) < COLLECTION_MOVE_THRESHOLD && Math.abs(dy) < COLLECTION_MOVE_THRESHOLD) return;

      moved.push({ item, dx, dy });
    });

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }

    // Dawn's reveal opts out of reduced motion in CSS; this scripted move must check itself.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Translate only: a filter keeps the column count, so card size is unchanged.
    const running = moved.map(({ item, dx, dy }) =>
      item.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
        duration: COLLECTION_REFLOW_DURATION,
        easing: COLLECTION_REFLOW_EASING,
      })
    );

    if (!running.length) return;

    this.cancelReflow = () => {
      running.forEach((animation) => animation.cancel());
      this.cancelReflow = null;
    };
  }

  /* ------------------------------------------------------------------
   * Sub collection swap
   * ------------------------------------------------------------------ */

  // The nav itself is never swapped: a child collection has none (see decisions.md: sub-collection nav).

  // The active link goes to the parent; shared with the prefetch so both use one URL.
  subcollectionTarget(link) {
    const nav = link.closest('[data-subcollection-nav]');

    if (!nav) return null;

    const active = link.classList.contains('is-active');
    const url = active ? nav.dataset.parentUrl : link.getAttribute('href');

    if (!url) return null;

    return {
      url,
      title: active ? nav.dataset.parentTitle : link.textContent.trim(),
      target: active ? null : link,
    };
  }

  selectSubcollection(link) {
    const destination = this.subcollectionTarget(link);

    if (!destination) return;

    this.swapCollection(destination.url, destination);
  }

  /* ------------------------------------------------------------------
   * Sub collection hover prefetch
   * ------------------------------------------------------------------ */

  subcollectionUrlFor(node) {
    const link = node.closest?.('[data-subcollection-link]');

    return link ? this.subcollectionTarget(link)?.url ?? null : null;
  }

  // Hover/focus is a guess: wait out the delay and respect metered connections.
  queueSubcollectionPrefetch(node) {
    if (collectionPrefetchUnwelcome()) return;

    const url = this.subcollectionUrlFor(node);

    if (!url) return;

    // One shared timer, so a sweeping pointer only warms where it settles.
    window.clearTimeout(this.subcollectionHoverTimer);

    this.subcollectionHoverTimer = window.setTimeout(
      () => this.prefetchSubcollection(url),
      COLLECTION_SUBNAV_HOVER_DELAY
    );
  }

  // A press is committed: no delay, no connection check.
  commitSubcollectionPrefetch(node) {
    const url = this.subcollectionUrlFor(node);

    if (!url) return;

    // Clear the hover timer: browsers fire a compatibility pointerover on tap.
    window.clearTimeout(this.subcollectionHoverTimer);

    this.prefetchSubcollection(url);
  }

  async prefetchSubcollection(url) {
    const key = this.subcollectionKey(url);

    if (!key || this.subcollectionRequest(url)) return;

    const controller = new AbortController();
    const request = this.fetchPage(key, controller.signal);
    const entry = { request, at: Date.now(), controller, settled: false };

    this.subcollectionCache.set(key, entry);

    try {
      // Parsed markup is held under a TTL; image bytes go to the browser cache for the session.
      const document_ = await request;

      // Landed: too late to abort, and the response is kept.
      entry.settled = true;

      this.warmFirstImages(document_, this.swapPrefetchCount());
    } catch (error) {
      // Drop the entry on failure so a later click starts fresh.
      this.subcollectionCache.delete(key);
    }
  }

  abortSubcollectionPrefetch(node) {
    const url = this.subcollectionUrlFor(node);
    const key = url && this.subcollectionKey(url);
    const entry = key && this.subcollectionCache.get(key);

    // A landed response is kept; the bytes are already paid for.
    if (!entry || entry.settled) return;

    entry.controller.abort();
    this.subcollectionCache.delete(key);
  }

  subcollectionKey(url) {
    const gridSection = this.dataset.sectionId;

    if (!gridSection) return null;

    if (new URL(url, window.location.origin).pathname === this.collectionUrl) return null;

    return this.sectionUrl(url, gridSection);
  }

  subcollectionRequest(url) {
    const key = this.subcollectionKey(url);
    const entry = key && this.subcollectionCache.get(key);

    if (!entry) return null;

    if (Date.now() - entry.at > COLLECTION_SUBNAV_CACHE_TTL) {
      this.subcollectionCache.delete(key);
      return null;
    }

    return entry.request;
  }

  // Registered before facets.js; setting searchParamsPrev makes its popstate handler bail.
  onPopState() {
    const path = window.location.pathname;

    if (path === this.collectionUrl) return;

    const search = window.location.search;

    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsPrev = search.slice(1);
    }

    const nav = this.querySelector('[data-subcollection-nav]');
    const links = nav ? Array.from(nav.querySelectorAll('[data-subcollection-link]')) : [];
    const target = links.find((link) => new URL(link.href, window.location.origin).pathname === path) ?? null;

    this.swapCollection(path + search, {
      title: target ? target.textContent.trim() : nav?.dataset.parentTitle,
      target,
      push: false,
    });
  }

  async swapCollection(url, { title, target = null, push = true } = {}) {
    const path = new URL(url, window.location.origin).pathname;

    if (path === this.collectionUrl) return;

    const gridSection = this.dataset.sectionId;

    if (!gridSection || !this.gridContainer) {
      window.location.href = url;
      return;
    }

    // The latest click wins; a response against a stale token is dropped.
    const token = this.swapToken + 1;

    this.swapToken = token;

    // Clear the hover timer: a tap's compatibility pointerover would fetch again.
    window.clearTimeout(this.subcollectionHoverTimer);

    const nav = this.querySelector('[data-subcollection-nav]');
    const facets = this.querySelector('.collection-toolbar__facets');

    // The underline moves on click, not on response.
    this.markSubcollectionActive(nav, target);
    nav?.setAttribute('aria-busy', 'true');

    // Fade only the grid, and keep its height so the page does not collapse.
    this.gridContainer.classList.add('collection-swap-fade', 'collection-swap-fade--hidden');

    // Only shown if the response has not landed by then.
    const loaderTimer = window.setTimeout(() => {
      if (this.swapToken === token) this.showSwapLoader(true);
    }, COLLECTION_SWAP_LOADER_DELAY);

    try {
      // Await an in-flight prefetch; the fade runs alongside the request.
      const request = this.subcollectionRequest(url) ?? this.fetchPage(this.sectionUrl(url, gridSection));

      const [document_] = await Promise.all([request, collectionWait(this.swapFadeDuration())]);

      window.clearTimeout(loaderTimer);

      if (this.swapToken !== token) return;

      this.applySwap(document_, { url, path, title, nav, facets, push });
    } catch (error) {
      console.error('[Collection subcollection swap]', error);

      // Fall back to plain navigation.
      window.location.href = url;
    } finally {
      window.clearTimeout(loaderTimer);

      // Only the swap that still owns the element cleans up.
      if (this.swapToken === token) {
        this.showSwapLoader(false);
        nav?.removeAttribute('aria-busy');
      }
    }
  }

  showSwapLoader(visible) {
    const loader = this.querySelector('[data-collection-swap-loader]');

    if (!loader) return;

    // Read on show: the faded grid still holds its real offset.
    if (visible && this.gridContainer) loader.style.top = `${this.gridContainer.offsetTop}px`;

    loader.classList.toggle('is-visible', visible);
  }

  applySwap(gridDocument, { url, path, title, nav, facets, push }) {
    const nextGrid = gridDocument.getElementById('ProductGridContainer');

    if (!nextGrid) throw new Error('Product grid container not found in response');

    // Empty Map: every card is new, so it gets the page-load reveal and no FLIP.
    this.previousCardPositions = new Map();
    this.gridContainer.innerHTML = nextGrid.innerHTML;

    // Replaced wholesale: there is nothing to reconcile across collections.
    const nextFacets = gridDocument.querySelector('.collection-toolbar__facets');

    if (facets && nextFacets) {
      facets.innerHTML = nextFacets.innerHTML;

      // see decisions.md: injected facets markup has .scroll-trigger stripped
      facets.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.remove('scroll-trigger', 'scroll-trigger--offscreen');
      });

      // see known-issues.md: facets.js initialises state on DOMContentLoaded
      if (typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.syncFacetDetailsState();
        FacetFiltersForm.updateAppliedFilterCount();
      }
    }

    if (push) history.pushState({ searchParams: '' }, '', url);

    this.collectionUrl = path;

    if (title) document.title = this.swapTitle(nav, title);

    // A swap clears filters; facets.js checks this before its own popstate render.
    if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = '';

    // Container opaque instantly so its fade doesn't stack with the cards' reveal.
    if (this.gridContainer.querySelector('.grid__item.scroll-trigger')) {
      this.gridContainer.classList.remove('collection-swap-fade', 'collection-swap-fade--hidden');
      return;
    }

    // No card reveal to stack with, so the container fades itself in.
    this.gridContainer.classList.remove('collection-swap-fade--hidden');

    window.setTimeout(() => {
      this.gridContainer.classList.remove('collection-swap-fade');
    }, this.swapFadeDuration() + 50);
  }

  markSubcollectionActive(nav, link) {
    if (!nav) return;

    nav.querySelectorAll('[data-subcollection-link]').forEach((item) => {
      const isActive = item === link;

      item.classList.toggle('is-active', isActive);

      item.classList.toggle('link--underline', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  // Mirrors theme.liquid's <title>.
  swapTitle(nav, title) {
    const suffix = nav?.dataset.titleSuffix;

    if (!suffix || title.indexOf(suffix) !== -1) return title;

    return `${title} – ${suffix}`;
  }

  // Read the live view; the attribute is set at parse time.
  swapPrefetchCount() {
    const columns = Number(this.dataset.collectionGridColumns) || 4;

    return columns * COLLECTION_SWAP_PREFETCH_ROWS;
  }

  swapFadeDuration() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : COLLECTION_SWAP_FADE;
  }

  sectionUrl(url, sectionId) {
    const next = new URL(url, window.location.origin);

    next.searchParams.set('section_id', sectionId);

    return next.toString();
  }

  /* ------------------------------------------------------------------
   * Load More
   * ------------------------------------------------------------------ */

  async loadMore(button, { fromKeyboard = false } = {}) {
    if (button.dataset.loading === 'true') return;

    const grid = this.querySelector('#product-grid');
    const url = this.buildPageUrl(button);

    if (!grid || !url) return;

    const wrapper = button.closest('[data-collection-load-more-wrapper]');
    const label = button.querySelector('[data-load-more-text]');
    const originalText = label ? label.textContent : 'Load More';

    this.setLoadMoreLoading(button, label, true, 'Loading...');

    try {
      const document = await this.fetchPage(url);
      const newGrid = document.querySelector('#product-grid');

      if (!newGrid) throw new Error('Product grid not found in response');

      const addedItems = Array.from(newGrid.children);

      addedItems.forEach((item) => grid.appendChild(item));

      if (typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.syncFacetDetailsState();
      }

      const nextButton = document.querySelector('[data-collection-load-more]');

      if (nextButton?.dataset.nextUrl) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl;
        this.setLoadMoreLoading(button, label, false, originalText);
      } else {
        // Last page.
        wrapper?.remove();
      }

      // Re-arm the prefetch for the button's new data-next-url.
      this.syncPrefetchObserver();

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }

      this.focusFirstAddedProduct(addedItems, fromKeyboard);

      this.dispatchEvent(
        new CustomEvent('collection:products:appended', {
          bubbles: true,
          detail: { sectionId: button.dataset.sectionId, addedItems },
        })
      );
    } catch (error) {
      console.error('[Collection Load More]', error);
      this.setLoadMoreLoading(button, label, false, originalText);

      // Disabling the button for the request drops focus to <body>.
      if (document.activeElement === document.body) button.focus();
    }
  }

  // see known-issues.md: Load More moves focus
  focusFirstAddedProduct(addedItems, fromKeyboard) {
    const link = addedItems
      .map((item) => item.querySelector('.product_card_link'))
      .find(Boolean);

    if (!link) return;

    link.focus({ preventScroll: !fromKeyboard });
  }

  setLoadMoreLoading(button, label, loading, text) {
    button.dataset.loading = String(loading);
    button.disabled = loading;
    button.classList.toggle('is-loading', loading);

    if (label) label.textContent = text;
  }

  // section_id makes Shopify return this section alone.
  buildPageUrl(button) {
    const { nextUrl, sectionId } = button.dataset;

    if (!nextUrl || !sectionId) return null;

    const url = new URL(nextUrl, window.location.origin);

    url.searchParams.set('section_id', sectionId);

    return url.toString();
  }

  async fetchPage(url, signal) {
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });

    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /* ------------------------------------------------------------------
   * Next-page image prefetch
   * ------------------------------------------------------------------ */

  // Re-observing re-delivers an entry, so this doubles as a re-check.
  syncPrefetchObserver() {
    if (this.observedButton) this.prefetchObserver.unobserve(this.observedButton);

    this.observedButton = this.querySelector('[data-collection-load-more]');

    if (this.observedButton) this.prefetchObserver.observe(this.observedButton);
  }

  // Only image bytes: section HTML is no-cache and can go stale.
  async prefetchNextPage(button) {
    const nextUrl = button.dataset.nextUrl;
    const url = this.buildPageUrl(button);

    if (!url || this.prefetchedPages.has(nextUrl)) return;

    this.prefetchedPages.add(nextUrl);

    try {
      this.warmFirstImages(await this.fetchPage(url));
    } catch (error) {
      // Optimisation only: fail quietly and let a later approach retry.
      this.prefetchedPages.delete(nextUrl);
    }
  }

  warmFirstImages(document, count = COLLECTION_PREFETCH_CARD_COUNT) {
    const cards = Array.from(document.querySelectorAll('#product-grid > *')).slice(0, count);

    cards.forEach((card) => {
      // The first img is the first gallery slide.
      const source = card.querySelector('img');

      if (!source?.src) return;

      // Mirror sizes and srcset before src, or the warmed URL is not the rendered one.
      const image = new Image();

      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;

      image.src = source.src;
    });
  }
}

customElements.define('collection-view', CollectionView);
